import { Fragment } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { prettyLogEvent } from "@internal/analytics";
import { CreativePlatformEnum } from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { AttributionModelSelect, DateRangeSelect } from "@shared/selects";
import { type OptionType } from "@shared/selects/select";
import { renderChevronAnimation } from "@shared/selects/utils";

import {
  useReportBodyState,
  type ReportBodyState,
} from "../hooks/ca-control-state";

import {
  AppLovinIcon,
  FBIcon,
  GoogleIcon,
  PinterestIcon,
  SnapchatIcon,
  TikTokIcon,
  YoutubeIcon,
} from "./icons";

export interface ReportBodyControlProps {
  isLoading: boolean;
}

export const ReportBodyControl = ({ isLoading }: ReportBodyControlProps) => {
  const { user } = useUser();
  const {
    state,

    attributionModel,
    setAttributionModel,

    attributionWindow,
    setAttributionWindow,

    setDateRange,
  } = useReportBodyState();

  const maxLookbackDays = 20 * 365;

  if (user.featureFlags.enableDashboardV2) {
    return (
      <div className="flex space-x-3 ml-5 max-w-[25rem] w-full items-end mr-2">
        <DateRangeSelect
          isLoading={isLoading}
          maxLookbackDays={maxLookbackDays}
          dateRange={state.dateRange}
          onUpdate={(v) => {
            setDateRange(v);
            prettyLogEvent.onClick({
              user,
              page: Pages.CREATIVE_ANALYTICS,
              additionalActionInfo: "Date range",
              data: v,
            });
          }}
          showDateRangeDisplay={false}
          className="min-w-[15rem]"
        />
        <PlatformSelector />
      </div>
    );
  }

  return (
    <div className="flex space-x-3 ml-5 max-w-[50rem] w-full items-end mr-2">
      <DateRangeSelect
        isLoading={isLoading}
        maxLookbackDays={maxLookbackDays}
        dateRange={state.dateRange}
        onUpdate={(v) => {
          setDateRange(v);
          prettyLogEvent.onClick({
            user,
            page: Pages.CREATIVE_ANALYTICS,
            additionalActionInfo: "Date range",
            data: v,
          });
        }}
        showDateRangeDisplay={false}
        className="min-w-[15rem]"
      />
      <PlatformSelector />
      <AttributionModelSelect
        isLoading={isLoading}
        attributionModel={attributionModel}
        onChange={(v) => {
          setAttributionModel(v);
          prettyLogEvent.onClick({
            user,
            page: Pages.CREATIVE_ANALYTICS,
            additionalActionInfo: "Attribution Model",
            data: v,
          });
        }}
      />
      <AttributionWindowSelect
        isLoading={isLoading}
        attributionModel={attributionModel}
        attributionWindow={attributionWindow}
        timeGranularity="daily"
        onChange={(v) => {
          setAttributionWindow(v);
          prettyLogEvent.onClick({
            user,
            page: Pages.CREATIVE_ANALYTICS,
            additionalActionInfo: "Attribution Window",
            data: v,
          });
        }}
      />
    </div>
  );
};

function getPlatformIcon(platform: CreativePlatformEnum) {
  if (platform === CreativePlatformEnum.youtube) {
    return <YoutubeIcon key={platform} />;
  }
  if (platform === CreativePlatformEnum.adwords) {
    return <GoogleIcon key={platform} />;
  }
  if (platform === CreativePlatformEnum.pinterest) {
    return <PinterestIcon key={platform} />;
  }
  if (platform === CreativePlatformEnum.facebook) {
    return <FBIcon key={platform} />;
  }
  if (platform === CreativePlatformEnum.snapchat) {
    return <SnapchatIcon key={platform} />;
  }
  if (platform === CreativePlatformEnum.applovin) {
    return <AppLovinIcon key={platform} />;
  }
  return <TikTokIcon key={platform} />;
}

export function makeReportBodyStateQuery(
  state: ReportBodyState,
): URLSearchParams {
  const search = new URLSearchParams();
  search.set("attributionModel", state.attributionModel);
  search.set("attributionWindow", state.attributionWindow);

  search.set("dateRange", JSON.stringify(state.dateRange));

  return search;
}

const options = [
  { label: "Facebook", value: CreativePlatformEnum.facebook },
  { label: "Snapchat", value: CreativePlatformEnum.snapchat },
  { label: "TikTok", value: CreativePlatformEnum.tiktok },
  { label: "Pinterest", value: CreativePlatformEnum.pinterest },
  { label: "Google", value: CreativePlatformEnum.adwords },
  { label: "YouTube", value: CreativePlatformEnum.youtube },
  { label: "AppLovin", value: CreativePlatformEnum.applovin },
];

function PlatformSelector() {
  const { user } = useUser();

  const platformOptions = options.map((o) => ({
    label: o.label,
    value: o.value,
  })) as OptionType<CreativePlatformEnum>[];

  const { selectedPlatforms, setSelectedPlatforms } = useReportBodyState();

  const moveIcon = (index: number) => ({
    "left-[1.75rem]": index === 1,
    "left-[3rem]": index === 2,
    "left-[4.25rem]": index === 3,
    "left-[5.5rem]": index === 4,
    "left-[6.75rem]": index === 5,
  });

  const selectedPlatformValues = selectedPlatforms;

  return (
    <div className="flex flex-col w-full ml-5 mt-0.5">
      <div className="font-normal text-xs text-semibold text-text mb-1 text-v2-primary">
        PLATFORM
      </div>
      <Popover className="relative">
        {({ open }) => (
          <>
            <PopoverButton
              className={clsx(
                "h-[2rem] bg-v2-primary border-[1px] rounded-md px-2 w-full text-left flex flex-row border-v2-secondary text-v2-primary",
                "items-center relative min-w-[10rem]",
                {
                  "border border-v2-action ring-1 ring-cosmic": open,
                  "border-v2-primary": !open,
                },
              )}
            >
              <div>
                {selectedPlatforms.map((platform, index) => {
                  if (platform === CreativePlatformEnum.youtube) {
                    return (
                      <YoutubeIcon
                        key={platform}
                        className={clsx("absolute top-1", moveIcon(index))}
                      />
                    );
                  }
                  if (platform === CreativePlatformEnum.adwords) {
                    return (
                      <GoogleIcon
                        key={platform}
                        className={clsx("absolute top-1 ", moveIcon(index))}
                      />
                    );
                  }
                  if (platform === CreativePlatformEnum.pinterest) {
                    return (
                      <PinterestIcon
                        key={platform}
                        className={clsx(
                          "absolute top-1 border-[1px] border-white",
                          moveIcon(index),
                        )}
                      />
                    );
                  }
                  if (platform === CreativePlatformEnum.facebook) {
                    return (
                      <FBIcon
                        key={platform}
                        className={clsx(
                          "absolute top-1 border-[1px] border-white",
                          moveIcon(index),
                        )}
                      />
                    );
                  }
                  if (platform === CreativePlatformEnum.snapchat) {
                    return (
                      <SnapchatIcon
                        key={platform}
                        className={clsx(
                          "absolute top-1 border-[1px] border-white",
                          moveIcon(index),
                        )}
                      />
                    );
                  }
                  if (platform === CreativePlatformEnum.applovin) {
                    return (
                      <AppLovinIcon
                        key={platform}
                        className={clsx(
                          "absolute top-1 border-[1px] border-white",
                          moveIcon(index),
                        )}
                      />
                    );
                  }
                  return (
                    <TikTokIcon
                      key={platform}
                      className={clsx(
                        "absolute top-1 border-[1px] border-white",
                        moveIcon(index),
                      )}
                    />
                  );
                })}
              </div>
              <span className={clsx({ "text-cosmic+1": open }, "pl-2 ml-auto")}>
                {renderChevronAnimation(open, false)}
              </span>
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                className={clsx(
                  "absolute w-[12rem] z-10 mt-2  bg-v2-primary",
                  "rounded-lg elevation-1 flex flex-col px-3 py-4",
                )}
              >
                <div>
                  {platformOptions.map((option) => {
                    const selected = selectedPlatformValues.includes(
                      option.value,
                    );
                    return (
                      <div key={option.value}>
                        <label className="flex flex-row items-center m-2 ml-3 accent-cosmic">
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={
                              selected && selectedPlatformValues.length === 1
                            }
                            onChange={() => {
                              const currentSet = new Set<CreativePlatformEnum>(
                                selectedPlatforms,
                              );

                              if (currentSet.has(option.value)) {
                                currentSet.delete(option.value);
                              } else {
                                currentSet.add(option.value);
                              }

                              const newSelectedPlatforms = [...currentSet];
                              setSelectedPlatforms(newSelectedPlatforms);
                              prettyLogEvent.onSelect({
                                user,
                                page: Pages.CREATIVE_ANALYTICS,
                                additionalActionInfo: "platform-selector",
                                data: newSelectedPlatforms,
                              });
                            }}
                            className="mr-3"
                          />
                          <div className="mr-2">
                            {getPlatformIcon(option.value)}
                          </div>
                          {option.label}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
}
